import React, { Component, Fragment } from "react";
import "./search.css";
import config from "../../config";
import Popup from "reactjs-popup";
import Header from "../header";
import EmailPopup from "../emailpopup";
import { Auth } from "aws-amplify";
import ExpungementPopup from "../expungementpopup";
import FileProperties from "../Search/filePropertiesPopup";
import ChatSearch, {
  CHAT_SEARCH_MODE_SEMANTIC,
  CHAT_SEARCH_MODE_ASSISTANT,
  isAiAllowListed,
} from "./ChatSearch";
import AppendPrependPdf from "../Browse/appenPrependPdf";
import SearchModeTabs from "./SearchModeTabs";
import ScopeBar from "./ScopeBar";
import IndexSearchPanel, { SCORE_COLUMN_NAME } from "./IndexSearchPanel";
import TextSearchPanel from "./TextSearchPanel";
import SemanticSearchPanel from "./SemanticSearchPanel";
import AiAssistantPanel from "./AiAssistantPanel";
import AiLockedPanel from "./AiLockedPanel";
import SearchHelpCard from "./SearchHelpCard";
import ResultsGrid from "./ResultsGrid";
import {
  SEARCH_MODES,
  MODE_LABELS,
  ALL_REPOSITORIES,
  ALL_TEMPLATES,
  AI_MODES,
  aiAccessState,
  aiTabLockReason,
  unavailableEntitlement,
  templateDisplayName,
  highlightToSafeHtml,
  highlightToText,
  escapeHtml,
  formatCount,
  matchTierLabel,
  MATCH_TIER_HINTS,
  shortHighlightHtml,
  formatDocumentDate,
  interpretedAs,
  semanticResultsSummary,
  semanticResultsNotes,
  blockLotSearchError,
} from "./searchModes";
import {
  templateHasOcredFiles,
  getTemplates,
  getTemplate,
  searchTemplate,
  updateFileName,
  getUserProfile,
  getRedactionColor,
  getRepositoriesForChatSearch,
  getTemplatesBasedOnRepositoryId,
  chatSearchDocuments,
  searchByBlockAndLot,
  getEntitlements,
} from "../../libs/API";

const TEXT_SEARCH_MAX_RESULTS = 2000;

/**
 * The Search screen: one page, four modes (Index, Text, Semantic, AI Assistant) under a shared scope bar.
 * Nothing opens in another window any more; the viewer stays an overlay on this page in every mode.
 */
class Search extends Component {
  gridRef = null;
  chatSearchRef = null;
  isViewerOpening = false;

  constructor(props) {
    super(props);
    this.state = {
      mode: SEARCH_MODES.INDEX,

      // Scope bar
      repositories: [],
      templates: [],
      repositoryTemplates: null, // table names available in the selected repository, null = all
      scopeRepositoryId: ALL_REPOSITORIES,
      scopeTemplateName: ALL_TEMPLATES,

      // Entitlements (GET api/Entitlements/me)
      entitlement: null,
      entitlementLoading: true,
      deniedReason: null,

      // Index Search
      columns: [],
      search: {},
      showTextSearch: false,
      templateColumnsToShow: [],
      fileSize: true,
      pageCount: true,
      pageDimensions: true,
      textScore: true,
      redactionColumn: true,
      // "template" = one template's fields; "blockLot" = Block & Lot across all templates ("All index templates").
      indexResultKind: "template",
      blockLot: { block: "", lot: "" },
      blockLotError: "",
      blockLotInfo: null,

      // Text Search: offered with the text search role once any template the user can see has OCR text.
      anyOcrTemplate: false,
      textQuery: "",
      textMatchMode: "all",
      textHighlight: true,
      lastTextQuery: "",

      // Semantic Search and AI Assistant: one ChatSearch session shared by both tabs.
      semanticQuery: "",
      semanticMeta: null,
      aiSession: null,
      aiSearching: false,
      assistantQuestion: "",
      conversation: [],
      chatMode: CHAT_SEARCH_MODE_SEMANTIC,

      // Results (per mode) and grid state; "searched" = a search of that mode finished since the last reset.
      results: { index: [], text: [], semantic: [] },
      searched: { index: false, text: false },
      isLoading: false,
      renameMode: false,

      // Viewer and popups (unchanged from the previous screen)
      viewerOpen: false,
      fileIdArray: [],
      fileName: "",
      showEmailPopup: false,
      showExpungementPopup: false,
      showFilePropertiesPopup: false,
      showAppendPrependPopup: false,
      client: "",
      redactionColor: "",
    };
  }

  // ---------------------------------------------------------------------------------------------------------
  // Lifecycle and data loading
  // ---------------------------------------------------------------------------------------------------------

  async componentDidMount() {
    this.loadEntitlement();
    this.loadRepositories();
    await this.loadTemplates();

    const p = this.props.props || {};
    let clientName = "";
    if (p.hasCliftonWorkflow) clientName = "clifton";
    else if (p.hasMedfordWorkflow) clientName = "medford";
    else if (p.hasEnglewoodWorkflow) clientName = "englewood";
    else if (p.hasGlenrockWorkflow) clientName = "glenrock";
    else if (p.hasAberdeenWorkflow) clientName = "Aberdeen";
    else if (p.hasDemoExpungementWorkflow) clientName = "Demo";
    else if (p.hasEastamptonExpungementWorkflow) clientName = "Eastampton";
    else if (p.hasSomervilleExpungementWorkflow) clientName = "Somerville";
    else if (p.hasRaritanExpungementWorkflow) clientName = "Raritan";
    else if (p.hasLyndhurstWorkflow) clientName = "Lyndhurst";
    else if (p.hasWayneWorkflow) clientName = "Wayne";
    this.setState({ client: clientName });

    try {
      const response = await getRedactionColor(p.loggedInUser);
      this.setState({
        redactionColor: (response && response.data && response.data.redactionColor) || "Black",
      });
    } catch (error) {
      this.setState({ redactionColor: "Black" });
    }
  }

  loadEntitlement = async () => {
    this.setState({ entitlementLoading: true });
    try {
      const entitlement = await getEntitlements();
      this.setState({
        entitlement: entitlement || unavailableEntitlement(this.hasTextSearchRole()),
        entitlementLoading: false,
        deniedReason: null,
      });
    } catch (error) {
      console.warn("Entitlement lookup failed; AI tabs shown locked.", error);
      this.setState({
        entitlement: unavailableEntitlement(this.hasTextSearchRole()),
        entitlementLoading: false,
      });
    }
  };

  loadRepositories = async () => {
    try {
      const repositories = await getRepositoriesForChatSearch();
      this.setState({ repositories: Array.isArray(repositories) ? repositories : [] });
    } catch (error) {
      console.error("Repositories could not be loaded", error);
      this.setState({ repositories: [] });
    }
  };

  loadTemplates = async () => {
    try {
      const data = await getTemplates();
      const templates = Array.isArray(data)
        ? data.map((template) => ({
            ...template,
            displayName: String(template.displayName || template.tableName || "").trim(),
          }))
        : [];
      this.setState({ templates }, () => {
        // A user with exactly one template gets it selected, as before.
        if (templates.length === 1) this.handleTemplateChange(templates[0].tableName);
        this.checkAnyOcrTemplate(templates);
      });
    } catch (error) {
      console.error("Templates could not be loaded", error);
    }
  };

  hasTextSearchRole = () => Boolean(this.props.props && this.props.props.hasTextSearch);

  // The Text Search tab needs the role and at least one template with OCR text. Templates are checked one at a time
  // with the same call Index Search uses, stopping at the first that has OCR text. A global text search then covers
  // every template; templates without OCR text simply return nothing, as the earlier global search did.
  checkAnyOcrTemplate = async (templates) => {
    if (!this.hasTextSearchRole()) return;
    for (let i = 0; i < templates.length; i += 1) {
      const templateId = templates[i] && templates[i].templateId;
      if (!templateId) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        if ((await templateHasOcredFiles(templateId)) === "true") {
          this.setState({ anyOcrTemplate: true });
          return;
        }
      } catch (error) {
        console.warn("OCR check failed for a template", error);
      }
    }
  };

  textTabAvailable = () => this.hasTextSearchRole() && this.state.anyOcrTemplate;

  isAllowListed = () => isAiAllowListed((this.props.props || {}).loggedInUser);

  // Live, locked, hidden or loading, per AI tab. While the server's seat gate is off only the allow-listed logins see
  // the AI tabs (today's rule); once it is on, the entitlement decides.
  aiState = (mode) => {
    if (this.state.entitlementLoading) return this.isAllowListed() ? "loading" : "hidden";
    return aiAccessState(mode, this.state.entitlement, this.isAllowListed());
  };

  // The shared AI session lives as long as at least one AI tab is usable.
  aiChatAvailable = () =>
    !this.state.entitlementLoading && AI_MODES.some((mode) => this.aiState(mode) === "live");

  // ---------------------------------------------------------------------------------------------------------
  // Scope
  // ---------------------------------------------------------------------------------------------------------

  availableTemplates = () => {
    const { templates, repositoryTemplates } = this.state;
    if (!repositoryTemplates) return templates;
    const allowed = new Set(repositoryTemplates.map((name) => String(name).toLowerCase()));
    const filtered = templates.filter(
      (template) => template.tableName && allowed.has(String(template.tableName).toLowerCase()),
    );
    return filtered.length > 0 ? filtered : templates;
  };

  selectedTemplate = () => {
    const name = this.state.scopeTemplateName;
    if (!name) return null;
    return (
      this.state.templates.find(
        (template) => String(template.tableName).toLowerCase() === String(name).toLowerCase(),
      ) || null
    );
  };

  selectedTemplateId = () => {
    const template = this.selectedTemplate();
    return template && template.templateId ? template.templateId : null;
  };

  scopeForChatSearch = () => ({
    repositoryId: this.state.scopeRepositoryId ? Number(this.state.scopeRepositoryId) : null,
    allRepositories: !this.state.scopeRepositoryId,
    templateId: this.selectedTemplateId(),
    templateName: this.state.scopeTemplateName || null,
  });

  handleRepositoryChange = async (repositoryId) => {
    this.setState({ scopeRepositoryId: repositoryId, repositoryTemplates: null });
    this.clearResults();
    if (!repositoryId) return;
    try {
      const rows = await getTemplatesBasedOnRepositoryId(repositoryId);
      const names = Array.isArray(rows)
        ? rows.map((row) => row.TableName || row.tableName).filter(Boolean)
        : [];
      this.setState((state) => {
        const stillSelected =
          !state.scopeTemplateName ||
          names.some((name) => String(name).toLowerCase() === String(state.scopeTemplateName).toLowerCase());
        return {
          repositoryTemplates: names,
          scopeTemplateName: stillSelected ? state.scopeTemplateName : ALL_TEMPLATES,
          columns: stillSelected ? state.columns : [],
        };
      });
    } catch (error) {
      console.error("Templates for repository could not be loaded", error);
    }
  };

  handleTemplateChange = async (tableName) => {
    this.clearResults();
    if (!tableName) {
      this.setState({
        scopeTemplateName: ALL_TEMPLATES,
        columns: [],
        search: {},
        showTextSearch: false,
      });
      return;
    }
    this.setState({ scopeTemplateName: tableName, search: {}, columns: [] });
    await this.loadSearchControls(tableName);
    await this.setShowTextSearch(tableName);
  };

  async loadSearchControls(tableName) {
    try {
      const columns = await getTemplate(tableName);
      if (this.state.scopeTemplateName === tableName) {
        this.setState({ columns: Array.isArray(columns) ? columns : [] });
      }
    } catch (error) {
      console.error("Template fields could not be loaded", error);
    }
  }

  async setShowTextSearch(tableName) {
    const template = this.state.templates.find((item) => item.tableName === tableName);
    const templateId = template && template.templateId;
    if (!templateId || !this.hasTextSearchRole()) {
      this.setState({ showTextSearch: false });
      return;
    }
    try {
      const data = await templateHasOcredFiles(templateId);
      this.setState({ showTextSearch: data === "true" });
    } catch (error) {
      this.setState({ showTextSearch: false });
    }
  }

  // ---------------------------------------------------------------------------------------------------------
  // Modes
  // ---------------------------------------------------------------------------------------------------------

  setMode = (mode, callback) => {
    if (mode === this.state.mode) {
      if (callback) callback();
      return;
    }
    // ChatSearch keeps showing the last AI tab's layout while another tab is open.
    const chatMode =
      mode === SEARCH_MODES.ASSISTANT
        ? CHAT_SEARCH_MODE_ASSISTANT
        : mode === SEARCH_MODES.SEMANTIC
          ? CHAT_SEARCH_MODE_SEMANTIC
          : this.state.chatMode;
    this.setState({ mode, chatMode, renameMode: false, deniedReason: null }, callback);
  };

  clearResults = () => {
    this.setState({
      results: { index: [], text: [], semantic: [] },
      searched: { index: false, text: false },
      semanticMeta: null,
      blockLotInfo: null,
      renameMode: false,
    });
    if (this.chatSearchRef) this.chatSearchRef.startNewSearch();
  };

  currentResults = () => this.state.results[this.state.mode] || [];

  setModeResults = (mode, rows, extra = {}, searched = false) => {
    this.setState((state) => ({
      results: { ...state.results, [mode]: rows },
      searched: { ...state.searched, [mode]: searched },
      ...extra,
    }));
  };

  // ---------------------------------------------------------------------------------------------------------
  // Index Search (ported from the previous screen)
  // ---------------------------------------------------------------------------------------------------------

  handleSearchChange = (id, value) => {
    const search = { ...(this.state.search || {}) };
    if (!value || id === value) {
      delete search[id];
    } else {
      search[id] = value;
    }
    this.setState({ search });
  };

  runIndexSearch = async () => {
    const tableName = this.state.scopeTemplateName;
    if (!tableName) return;
    this.setState({ isLoading: true, renameMode: false });
    await this.checkUserProfile(tableName);
  };

  checkUserProfile = async (tableName) => {
    const userProfile = {
      userId: this.props.props.userId,
      templateName: tableName,
    };
    try {
      const x = await getUserProfile(userProfile);
      if (x && x.data !== "" && x.data) {
        this.setState(
          {
            templateColumnsToShow: x.data.templateColumnsToShow || [],
            redactionColumn: x.data.redaction,
            fileSize: x.data.fileSize,
            textScore: x.data.textScore,
            pageDimensions: x.data.pageDimensions,
            pageCount: x.data.pageCount,
          },
          async () => {
            await this.searchTemplates(tableName);
          },
        );
        return;
      }
    } catch (error) {
      console.warn("User profile could not be loaded; using default columns.", error);
    }
    await this.searchTemplates(tableName);
  };

  searchTemplates = async (selectedTemplate) => {
    const { search } = this.state;
    let { columns } = this.state;
    // Field names and values are encoded so a value with "&" or "=" cannot break the query string.
    const searchQueryString = Object.keys(search)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(search[key])}`)
      .join("&");
    try {
      const data = await searchTemplate(selectedTemplate, searchQueryString);
      const searchResults = Array.isArray(data) ? data : [];
      searchResults.forEach((searchResult) => {
        if (searchResult.HasAnnotations == null) searchResult.HasAnnotations = false;
      });
      // Add or remove the 'Text Search Score' column depending on the results.
      if (searchResults.length > 0) {
        if (Object.keys(searchResults[0]).includes(SCORE_COLUMN_NAME)) {
          if (!columns.find((col) => col.InputName === SCORE_COLUMN_NAME)) {
            columns = columns.concat({
              InputName: SCORE_COLUMN_NAME,
              DataType: "double",
              DataTypeLength: null,
            });
          }
        } else if (columns.find((col) => col.InputName === SCORE_COLUMN_NAME)) {
          columns = columns.filter((col) => col.InputName !== SCORE_COLUMN_NAME);
        }
      }
      this.setState({ columns });
      this.setModeResults(SEARCH_MODES.INDEX, searchResults, { isLoading: false, indexResultKind: "template" }, true);
    } catch (error) {
      console.log(error);
      this.setState({ isLoading: false });
    }
  };

  handleIndexReset = () => {
    this.setState({
      search: {},
      blockLot: { block: "", lot: "" },
      blockLotError: "",
      blockLotInfo: null,
      renameMode: false,
    });
    this.setModeResults(SEARCH_MODES.INDEX, []);
  };

  // ---------------------------------------------------------------------------------------------------------
  // Block & Lot ("All index templates" in Index Search): the existing union search across every template that
  // has Block and Lot fields.
  // ---------------------------------------------------------------------------------------------------------

  handleBlockLotChange = (field, value) => {
    this.setState((state) => ({ blockLot: { ...state.blockLot, [field]: value }, blockLotError: "" }));
  };

  runBlockLotSearch = async () => {
    const block = (this.state.blockLot.block || "").trim();
    const lot = (this.state.blockLot.lot || "").trim();
    if (!block && !lot) {
      this.setState({ blockLotError: "Enter a Block, a Lot, or both to search." });
      return;
    }
    if (block.length > 100 || lot.length > 100) {
      this.setState({ blockLotError: "Block and Lot must each be 100 characters or fewer." });
      return;
    }
    const scope = this.scopeForChatSearch();
    this.setState({ isLoading: true, renameMode: false, blockLotError: "", blockLot: { block, lot } });
    try {
      const data = await searchByBlockAndLot({
        block: block || null,
        lot: lot || null,
        repositoryId: scope.allRepositories ? null : scope.repositoryId,
        allRepositories: scope.allRepositories,
      });
      const value = data && typeof data === "object" ? data : {};
      const results = Array.isArray(value.results) ? value.results : [];
      const rows = results.map(this.blockLotRowFromResult);
      this.setModeResults(
        SEARCH_MODES.INDEX,
        rows,
        {
          isLoading: false,
          indexResultKind: "blockLot",
          blockLotInfo: {
            block,
            lot,
            resultCount: Number(value.resultCount) || rows.length,
            isTruncated: value.isTruncated === true,
            templatesSearched: Number(value.templatesSearched) || 0,
          },
        },
        true,
      );
    } catch (error) {
      this.setModeResults(SEARCH_MODES.INDEX, [], {
        isLoading: false,
        blockLotInfo: null,
        blockLotError: blockLotSearchError(error),
      });
    }
  };

  blockLotRowFromResult = (item) => {
    const value = item || {};
    const templateName = value.templateName || value.TemplateName || "";
    return {
      FileId: value.fileId !== undefined ? value.fileId : value.FileId,
      FileName: value.fileName || value.FileName || "",
      TemplateId: value.templateId !== undefined ? value.templateId : value.TemplateId,
      TemplateName: templateName,
      "Index Template": templateDisplayName(templateName, this.state.templates),
      Repository: value.repositoryName || value.RepositoryName || "",
      HasAnnotations: Boolean(value.hasAnnotations || value.HasAnnotations),
      PageCount: value.pageCount !== undefined ? value.pageCount : value.PageCount,
    };
  };

  // ---------------------------------------------------------------------------------------------------------
  // Text Search (global or one template)
  // ---------------------------------------------------------------------------------------------------------

  runTextSearch = async () => {
    const query = (this.state.textQuery || "").trim();
    if (!query) return;
    const scope = this.scopeForChatSearch();
    this.setState({ isLoading: true, renameMode: false, lastTextQuery: query });
    try {
      const payload = {
        repositoryId: scope.repositoryId,
        allRepositories: scope.allRepositories,
        query,
        matchMode: this.state.textMatchMode || "all",
        includeHighlights: true,
        maxResults: TEXT_SEARCH_MAX_RESULTS,
      };
      if (scope.templateId) payload.templateId = scope.templateId;
      else if (scope.templateName) payload.templateName = scope.templateName;
      const data = await chatSearchDocuments(payload);
      const rows = (Array.isArray(data) ? data : (data && data.results) || []).map((item) =>
        this.textRowFromResult(item),
      );
      this.setModeResults(SEARCH_MODES.TEXT, rows, { isLoading: false }, true);
    } catch (error) {
      console.error("Text search failed", error);
      this.setModeResults(SEARCH_MODES.TEXT, [], { isLoading: false });
    }
  };

  textRowFromResult = (item) => {
    const value = item || {};
    const templateName = value.templateName || value.TemplateName || "";
    const highlights = Array.isArray(value.highlights) ? value.highlights : [];
    return {
      FileId: value.fileId !== undefined ? value.fileId : value.FileId,
      FileName: value.fileName || value.FileName || "",
      TemplateId: value.templateId !== undefined ? value.templateId : value.TemplateId,
      TemplateName: templateName,
      "Index Template": templateDisplayName(templateName, this.state.templates),
      Repository: value.repositoryName || value.RepositoryName || "",
      [SCORE_COLUMN_NAME]: value.textSearchScore !== undefined ? value.textSearchScore : value.TextSearchScore || 0,
      Highlights: highlights,
      "Matched text": highlights.map(highlightToText).join(" … "),
      HasAnnotations: Boolean(value.hasAnnotations || value.HasAnnotations),
      PageCount: value.pageCount !== undefined ? value.pageCount : value.PageCount,
    };
  };

  handleTextReset = () => {
    this.setState({ textQuery: "", lastTextQuery: "", renameMode: false });
    this.setModeResults(SEARCH_MODES.TEXT, []);
  };

  // ---------------------------------------------------------------------------------------------------------
  // Semantic Search and AI Assistant (embedded ChatSearch)
  // ---------------------------------------------------------------------------------------------------------

  runSemanticSearch = () => {
    const query = (this.state.semanticQuery || "").trim();
    if (!query || !this.chatSearchRef) return;
    this.setState({ aiSearching: true, renameMode: false });
    // Every press of "Search with AI" is a new search of the whole scope, never a follow-up on the last results.
    this.chatSearchRef.submitQuery(query, { fresh: true });
  };

  // Every opening search of the shared session lands here, whichever AI tab ran it, so the Semantic grid always
  // lists the session's records. Rows keep the server's order and the server's tier; nothing is re-scored here.
  handleSemanticDocuments = (documents, meta) => {
    const list = Array.isArray(documents) ? documents : [];
    const rows = list.map((document, index) => ({
      FileId: document.fileId,
      FileName: document.fileName || `File ${document.fileId}`,
      TemplateId: document.templateId,
      TemplateName: document.templateName || "",
      "Index Template": templateDisplayName(document.templateName, this.state.templates),
      Repository: document.repositoryName || "",
      Rank: index + 1,
      Match: matchTierLabel(document.qualificationTier),
      DocumentDate: formatDocumentDate(document.documentDate),
      PageCount: document.pageCount || null,
      "Why it matched": document.reason || "",
      Highlight: Array.isArray(document.highlights) && document.highlights.length > 0 ? document.highlights[0] : "",
      // As on Index Search, e-mail sends the redacted copy when this is true; a flag the server could not read counts
      // as redacted, never as unredacted.
      HasAnnotations: document.hasAnnotations !== false,
      __document: document,
    }));
    this.setModeResults(SEARCH_MODES.SEMANTIC, rows, { aiSearching: false, semanticMeta: meta || null });
  };

  // New search (Semantic) and New conversation (Assistant) both end the one shared session.
  startNewAiConversation = () => {
    this.setState({ semanticQuery: "", semanticMeta: null, aiSearching: false, renameMode: false });
    this.setModeResults(SEARCH_MODES.SEMANTIC, []);
    if (this.chatSearchRef) this.chatSearchRef.startNewSearch();
  };

  handleSessionChange = (sessionId) => {
    if (sessionId) {
      this.setState({ aiSession: sessionId });
      return;
    }
    // The session ended (new search, new conversation, scope change): its records leave the grid with it.
    this.setState({ aiSession: null, semanticMeta: null });
    this.setModeResults(SEARCH_MODES.SEMANTIC, []);
  };

  handleEntitlementDenied = (body) => {
    this.setState({
      deniedReason: (body && body.reason) || "FEATURE_NOT_INCLUDED",
      isLoading: false,
      aiSearching: false,
    });
    this.loadEntitlement();
  };

  // The left-hand "Your question" box of the AI Assistant: always a new search of the scope (follow-ups use the box
  // under the results), exactly as the Semantic tab's Search with AI.
  runAssistantQuestion = () => {
    const query = (this.state.assistantQuestion || "").trim();
    if (!query || !this.chatSearchRef) return;
    this.setState({ aiSearching: true, assistantQuestion: "" });
    this.chatSearchRef.submitQuery(query, { fresh: true });
  };

  // The search's own "broader matches" / "more results" actions, run by the shared session exactly as its buttons do.
  runChatAction = (actionType) => {
    if (!this.chatSearchRef) return;
    if (this.chatSearchRef.runLatestAction(actionType)) this.setState({ aiSearching: true });
  };

  handleRefine = () => {
    const box = document.getElementById("semanticSearchQuery");
    if (box) box.focus();
  };

  // The shared session's own error text for a failed opening search (its card is hidden on this tab). Read when the
  // session reports the search finished, which re-renders this screen.
  semanticSearchError = () => {
    const chat = this.chatSearchRef;
    // A search that answered (even with nothing found) is not a failure: only a search with no response at all.
    if (!chat || !chat.state || this.state.aiSearching || chat.state.activeSearchSessionId || this.state.semanticMeta) return "";
    return chat.state.aiError ? String(chat.state.aiError) : "";
  };

  // Nothing listed: said kindly, with the server's own explanation of what it searched.
  semanticEmptyText = () => {
    const answer = String((this.state.semanticMeta && this.state.semanticMeta.answer) || "").trim();
    // The server's own explanation is added unless it only repeats that nothing was found.
    const extra = answer && !/^i couldn.?t find/i.test(answer) ? ` ${answer}` : "";
    return `I couldn't find records for this search.${extra} Try fewer words, a different spelling, or another template.`;
  };

  // "This conversation": the opening question and every follow-up asked about its results. ChatSearch reports the
  // opening question(s) and the latest follow-up; a new opening question starts a new list.
  handleConversationChange = (questions) => {
    this.setState((state) => {
      const latest = Array.isArray(questions) ? questions : [];
      if (!latest.length) return { conversation: [] };
      const previous = state.conversation || [];
      if (!previous.length || previous[0] !== latest[0]) return { conversation: latest };
      const merged = previous.slice();
      latest.slice(1).forEach((question) => {
        if (merged[merged.length - 1] !== question) merged.push(question);
      });
      return { conversation: merged };
    });
  };

  // "Ask about these results": the same session, continued in the AI Assistant with a follow-up question.
  handleAskAboutResults = () => {
    this.setMode(SEARCH_MODES.ASSISTANT, () => {
      setTimeout(() => {
        if (this.chatSearchRef) this.chatSearchRef.focusChatInput();
      }, 0);
    });
  };

  // "Search for: ..." on a couldn't-find card: a new opening search, run on the Semantic Search tab with the
  // suggested words in its query box, so its records come with their tiers in the grid.
  handleSuggestedSearch = (text) => {
    const query = (text || "").trim();
    if (!query) return;
    this.setMode(SEARCH_MODES.SEMANTIC, () => this.setState({ semanticQuery: query }, this.runSemanticSearch));
  };

  // ---------------------------------------------------------------------------------------------------------
  // Grid columns per mode
  // ---------------------------------------------------------------------------------------------------------

  fileNameColumn = (renameFile, onOpen) => ({
    title: "File Name",
    field: "FileName",
    formatter: (cell) =>
      renameFile ? escapeHtml(cell.getValue()) : `<a href="#view">${escapeHtml(cell.getValue())}</a>`,
    editor: "input",
    headerFilter: "input",
    cellClick: (e, cell) => {
      if (!renameFile) onOpen(e, cell);
    },
  });

  selectionColumn = (renameFile) => ({
    formatter: "rowSelection",
    titleFormatter: "rowSelection",
    titleFormatterParams: { rowRange: "visible" },
    width: "3%",
    download: false,
    headerSort: false,
    visible: !renameFile,
    cellClick: (e, cell) => cell.getRow().toggleSelect(),
  });

  hiddenIdColumns = () => [
    { title: "File Type", field: "FileExtension", headerSort: false, width: "3%", visible: false, download: true },
    { title: "FileId", field: "FileId", headerSort: false, width: "3%", visible: false, download: true },
  ];

  mapIndexColumns = (renameFile) => {
    const p = this.props.props || {};
    const dateFormatter = (cell) => {
      let value = cell.getValue();
      const dateString = cell.getRow().getData().Date;
      if (value && dateString && value === dateString) {
        const date = new Date(value);
        const year = date.getUTCFullYear();
        let month = (date.getUTCMonth() + 1).toString();
        month = month.length > 1 ? month : "0" + month;
        let day = date.getUTCDate().toString();
        day = day.length > 1 ? day : "0" + day;
        value = month + "/" + day + "/" + year;
      }
      return value;
    };
    const boolFormatter = (cell) => (cell.getValue() === false ? "No" : "Yes");

    return [this.selectionColumn(renameFile)]
      .concat(this.hiddenIdColumns())
      .concat(this.fileNameColumn(renameFile, this.viewFile))
      .concat(
        this.state.columns.map((c, i) => {
          const col = {
            title: c.InputName,
            field: c.InputName,
            formatter: dateFormatter,
            visible: !this.state.templateColumnsToShow.includes(i.toString()),
          };
          if (c.InputName === SCORE_COLUMN_NAME) {
            col.tooltip = "Search Relevancy Score";
            col.visible = this.state.textScore;
          }
          return col;
        }),
      )
      .concat([
        {
          title: "Redaction",
          field: "HasAnnotations",
          formatter: boolFormatter,
          visible: Boolean(p.hasManageRedaction && this.state.redactionColumn),
        },
        {
          title: "Page Count",
          field: "PageCount",
          visible: Boolean(p.hasFileProperties && this.state.pageCount),
        },
        {
          title: "File Size (Bytes)",
          field: "FileSize",
          formatter: (cell) => (cell.getValue() != null ? cell.getValue().toLocaleString() : cell.getValue()),
          visible: Boolean(p.hasFileProperties && this.state.fileSize),
        },
        {
          title: "Page Dimensions",
          field: "PageDimensions",
          visible: Boolean(p.hasFileProperties && this.state.pageDimensions),
        },
      ]);
  };

  mapTextColumns = (renameFile) => {
    const p = this.props.props || {};
    return [this.selectionColumn(renameFile)]
      .concat(this.hiddenIdColumns())
      .concat(this.fileNameColumn(renameFile, this.viewTextResult))
      .concat([
        { title: "Index Template", field: "Index Template", headerFilter: "input" },
        { title: "Repository", field: "Repository", headerFilter: "input" },
        { title: "Score", field: SCORE_COLUMN_NAME, tooltip: "Search Relevancy Score", width: 90, hozAlign: "right" },
        {
          title: "Matched text",
          field: "Matched text",
          widthGrow: 3,
          cssClass: "ds-wrap",
          variableHeight: true,
          formatter: (cell) => {
            const highlights = cell.getRow().getData().Highlights || [];
            if (!highlights.length) return "";
            return `<span class="ds-snippet">${highlights.map(highlightToSafeHtml).join(" … ")}</span>`;
          },
          headerSort: false,
        },
        {
          title: "Redaction",
          field: "HasAnnotations",
          formatter: (cell) => (cell.getValue() === true ? "Yes" : "No"),
          visible: Boolean(p.hasManageRedaction),
        },
        { title: "Page Count", field: "PageCount", visible: Boolean(p.hasFileProperties), width: 110 },
      ]);
  };

  mapSemanticColumns = (renameFile) =>
    [this.selectionColumn(renameFile)]
      .concat(this.hiddenIdColumns())
      .concat([
        {
          title: "Rank",
          field: "Rank",
          width: 70,
          hozAlign: "right",
          sorter: "number",
          tooltip: "The order the search returned the records in",
        },
      ])
      .concat(this.fileNameColumn(renameFile, this.viewSemanticResult))
      .concat([
        {
          title: "Match",
          field: "Match",
          width: 105,
          headerFilter: "input",
          formatter: (cell) => {
            const label = String(cell.getValue() || "");
            return `<span class="ds-match ds-match-${escapeHtml(label.toLowerCase())}" title="${escapeHtml(
              MATCH_TIER_HINTS[label] || "",
            )}">${escapeHtml(label)}</span>`;
          },
        },
        { title: "Index Template", field: "Index Template", headerFilter: "input" },
        { title: "Repository", field: "Repository", headerFilter: "input" },
        {
          title: "Why it matched",
          field: "Why it matched",
          widthGrow: 3,
          headerSort: false,
          cssClass: "ds-wrap",
          variableHeight: true,
          // One short line: the server's reason, then a little of the matched text around its first highlighted word.
          formatter: (cell) => {
            const row = cell.getRow().getData();
            const reason = escapeHtml(cell.getValue() || "");
            const highlight = row.Highlight ? shortHighlightHtml(row.Highlight) : "";
            return `<span class="ds-reason">${reason}</span>${highlight ? ` <span class="ds-snippet">${highlight}</span>` : ""}`;
          },
        },
        {
          title: "Document Date",
          field: "DocumentDate",
          width: 120,
          visible: this.currentResults().some((row) => row.DocumentDate),
        },
        {
          title: "Page Count",
          field: "PageCount",
          width: 100,
          hozAlign: "right",
          visible: this.currentResults().some((row) => row.PageCount),
        },
      ]);

  mapBlockLotColumns = (renameFile) => {
    const p = this.props.props || {};
    return [this.selectionColumn(renameFile)]
      .concat(this.hiddenIdColumns())
      .concat(this.fileNameColumn(renameFile, this.viewSemanticResult))
      .concat([
        { title: "Index Template", field: "Index Template", headerFilter: "input" },
        { title: "Repository", field: "Repository", headerFilter: "input" },
        {
          title: "Redaction",
          field: "HasAnnotations",
          formatter: (cell) => (cell.getValue() === true ? "Yes" : "No"),
          visible: Boolean(p.hasManageRedaction),
        },
        { title: "Page Count", field: "PageCount", visible: Boolean(p.hasFileProperties), width: 110 },
      ]);
  };

  columnsForMode = (mode, renameFile) => {
    if (mode === SEARCH_MODES.TEXT) return this.mapTextColumns(renameFile);
    if (mode === SEARCH_MODES.SEMANTIC) return this.mapSemanticColumns(renameFile);
    if (this.state.indexResultKind === "blockLot") return this.mapBlockLotColumns(renameFile);
    return this.mapIndexColumns(renameFile);
  };

  columnsKey = (mode, renameFile) =>
    JSON.stringify([
      mode,
      renameFile,
      mode === SEARCH_MODES.INDEX ? this.state.indexResultKind : null,
      mode === SEARCH_MODES.SEMANTIC
        ? [this.currentResults().some((row) => row.DocumentDate), this.currentResults().some((row) => row.PageCount)]
        : null,
      mode === SEARCH_MODES.INDEX ? this.state.columns.map((c) => c.InputName) : null,
      this.state.templateColumnsToShow,
      this.state.redactionColumn,
      this.state.fileSize,
      this.state.pageCount,
      this.state.pageDimensions,
      this.state.textScore,
    ]);

  // ---------------------------------------------------------------------------------------------------------
  // Viewer (unchanged behaviour: iframe overlay on desktop, own tab on phones)
  // ---------------------------------------------------------------------------------------------------------

  async getRefreshToken() {
    const user = await Auth.currentAuthenticatedUser();
    return user.signInUserSession.refreshToken.token;
  }

  viewFile = (e, cell) => {
    const row = cell.getRow().getData();
    this.openDocument(
      { fileId: row.FileId, fileName: row.FileName },
      { textSearch: this.hasTextSearchRole() ? this.state.search.textSearch : null },
      e,
    );
  };

  viewTextResult = (e, cell) => {
    const row = cell.getRow().getData();
    this.openDocument(
      { fileId: row.FileId, fileName: row.FileName },
      { textSearch: this.state.textHighlight && this.hasTextSearchRole() ? this.state.lastTextQuery : null },
      e,
    );
  };

  viewSemanticResult = (e, cell) => {
    const row = cell.getRow().getData();
    this.openDocument({ fileId: row.FileId, fileName: row.FileName }, { textSearch: null }, e);
  };

  // Documents opened from the embedded ChatSearch (evidence cards, summaries, assistant tables). The cited page and the
  // record's OCR page count travel with it, so a citation opens on its page where the image has it (ac55, V1).
  handleChatViewDocument = (file, options = {}) => {
    if (!file) return;
    const viewerPayload = file.viewerPayload && typeof file.viewerPayload === "object" ? file.viewerPayload : {};
    const pageNumber = Number.isInteger(options.pageNumber) && options.pageNumber > 0 ? options.pageNumber : undefined;
    const pageCount = pageNumber && Number.isInteger(options.pageCount) && options.pageCount > 0 ? options.pageCount : undefined;
    const textSearch =
      options.suppressViewerSearch === true || !this.hasTextSearchRole()
        ? null
        : viewerPayload.textSearch || viewerPayload.searchText || viewerPayload.parsedTextSearch || null;
    this.openDocument(
      { fileId: file.fileId || file.FileId, fileName: file.fileName || file.FileName || "Document" },
      { textSearch, pageNumber, pageCount },
    );
  };

  openDocument = async (file, options = {}, event) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.isViewerOpening) return;
      this.isViewerOpening = true;
      setTimeout(() => (this.isViewerOpening = false), 1500);

      const p = this.props.props || {};
      const fileId = file.fileId;
      const fileName = file.fileName;
      const clientId = p.clientId;
      const userEmailId = p.loggedInUser;
      const targetOrigin = config.ImageViewer;
      const refreshToken = await this.getRefreshToken();
      const message = {
        refreshToken,
        fileId,
        editIndex: p.hasEditIndex,
        manageRedaction: p.hasManageRedaction,
        deletePages: p.hasDelete,
        textSearch: options.textSearch || null,
        fileName,
        userEmailId,
        managePii: p.hasPIIRole,
        clientId,
        printWithAnnotations: p.hasPrintWithAnnotationsRole,
        cantoggleRedactionColor: p.hasWhiteRedaction,
        redactionColor: this.state.redactionColor,
      };
      if (options.pageNumber) message.pageNumber = options.pageNumber;
      if (options.pageNumber && options.pageCount) message.pageCount = options.pageCount;

      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const viewerUrl = `${config.ImageViewer}?fileId=${encodeURIComponent(fileId)}&fileName=${encodeURIComponent(
          fileName,
        )}&clientId=${encodeURIComponent(clientId)}&userEmailId=${encodeURIComponent(
          userEmailId,
        )}&refreshToken=${encodeURIComponent(refreshToken)}${options.pageNumber ? `&pageNumber=${options.pageNumber}` : ""}${
          options.pageNumber && options.pageCount ? `&pageCount=${options.pageCount}` : ""
        }`;
        if (!window.__mobileViewerTab || window.__mobileViewerTab.closed) {
          window.__mobileViewerTab = window.open(viewerUrl, "mobileViewerTab", "noopener,noreferrer");
        } else {
          window.__mobileViewerTab.focus();
        }
        return;
      }

      this.setState({ viewerOpen: true });
      setTimeout(() => {
        const imageViewer = document.getElementById("imageViewer");
        if (!imageViewer) {
          console.error("imageViewer iframe not found after opening the viewer");
          return;
        }
        imageViewer.onload = null;
        const post = () => imageViewer.contentWindow.postMessage(JSON.stringify(message), targetOrigin);
        if (imageViewer.attachEvent) imageViewer.attachEvent("onload", post);
        else imageViewer.onload = post;
        imageViewer.src = config.ImageViewer;
      }, 300);
    } catch (err) {
      console.error("Error opening the viewer:", err);
    }
  };

  closeViewer = () => this.setState({ viewerOpen: false });

  // ---------------------------------------------------------------------------------------------------------
  // Toolbar actions (ported)
  // ---------------------------------------------------------------------------------------------------------

  handleBrowse = () => this.props.history.push("/browse");

  handleChildUnmount = () => {
    this.setState({
      showEmailPopup: false,
      showExpungementPopup: false,
      showFilePropertiesPopup: false,
      showAppendPrependPopup: false,
    });
  };

  handleEmailPopup = () => {
    const rows = this.gridRef ? this.gridRef.getSelectedData() : [];
    if (rows.length > 0) {
      const fileIds = rows.map((m) => ({ fileId: m.FileId, redaction: m.HasAnnotations }));
      this.setState({ showEmailPopup: true, fileIdArray: fileIds });
    }
  };

  handleExpungementPopup = () => {
    const rows = this.gridRef ? this.gridRef.getSelectedData() : [];
    if (rows.length === 1) {
      this.setState({ showExpungementPopup: true, fileIdArray: rows });
    } else if (rows.length > 1) {
      alert("You can expunge 1 file at a time.");
    }
  };

  handleFilePropertiesPopup = () => this.setState({ showFilePropertiesPopup: true });

  handleRenameFile = () => {
    const rows = this.gridRef ? this.gridRef.getSelectedData() : [];
    if (rows.length === 0) return;
    this.setState({ renameMode: true }, () => {
      const table = this.gridRef && this.gridRef.table;
      if (!table) return;
      table.setColumns(this.columnsForMode(this.state.mode, true));
      table.setData(rows);
      this.gridRef.setFooterText(1, rows.length);
    });
  };

  handleRenameFileCancel = (cancelFile) => {
    const table = this.gridRef && this.gridRef.table;
    if (table && cancelFile === false) {
      table.getEditedCells().forEach((cell) => {
        const oldVal = cell.getOldValue();
        if (oldVal !== null) {
          try {
            cell.restoreOldValue();
          } catch (ignored) {}
        }
      });
    }
    this.setState({ renameMode: false }, () => {
      const current = this.gridRef && this.gridRef.table;
      if (!current) return;
      current.setColumns(this.columnsForMode(this.state.mode, false));
      current.setData(this.currentResults());
      this.gridRef.setFooterText(1);
    });
  };

  handleRenameFileSave = async () => {
    const table = this.gridRef && this.gridRef.table;
    const editedCells = table ? table.getEditedCells() : [];
    if (editedCells.length > 0) {
      const mappedCells = editedCells.map((cell) => ({
        fileId: cell.getRow().getData().FileId,
        newfileName: cell.getValue().trim(),
      }));
      try {
        const data = await updateFileName(mappedCells);
        if (data.status === 200) {
          // Keep the new names in the mode's result rows so the grid shows them after leaving rename mode.
          const renamed = new Map(mappedCells.map((item) => [item.fileId, item.newfileName]));
          this.setModeResults(
            this.state.mode,
            this.currentResults().map((row) =>
              renamed.has(row.FileId) ? { ...row, FileName: renamed.get(row.FileId) } : row,
            ),
          );
          this.handleRenameFileCancel(true);
          return;
        }
      } catch (error) {
        console.error("Rename failed", error);
      }
    }
    this.handleRenameFileCancel(true);
  };

  handleDownloadIndexCsv = (e) => {
    if (e) e.preventDefault();
    const table = this.gridRef && this.gridRef.table;
    if (!table) return;
    const template = this.selectedTemplate();
    const baseName =
      this.state.mode === SEARCH_MODES.INDEX
        ? template
          ? template.displayName
          : "Block_Lot"
        : `${MODE_LABELS[this.state.mode]}${template ? `_${template.displayName}` : ""}`.replace(/[^\w-]+/g, "_");
    table.download("csv", `${baseName}_IndexData.csv`, { delimiter: "|" });
  };

  handleUploadFiles = (e) => {
    if (e) e.preventDefault();
    const rows = this.gridRef ? this.gridRef.getSelectedData() : [];
    if (rows.length > 1) {
      alert("You can Append or Prepend 1 file at a time.");
      return;
    }
    if (rows.length === 1) this.setState({ showAppendPrependPopup: true, fileIdArray: rows });
  };

  updateFileName = async (renameFilesParams) => {
    const data = await updateFileName(renameFilesParams);
    if (data.status === 200) this.handleRenameFileCancel(true);
  };

  // ---------------------------------------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------------------------------------

  scopeSummary = () => {
    const { mode, scopeRepositoryId, repositories } = this.state;
    const template = this.selectedTemplate();
    const repository = repositories.find(
      (item) => String(item.RepositoryId !== undefined ? item.RepositoryId : item.repositoryId) === String(scopeRepositoryId),
    );
    const repositoryName = repository ? repository.FolderName || repository.folderName || "Repository" : "All repositories";
    const templateCount = this.availableTemplates().length;
    if (mode === SEARCH_MODES.INDEX) {
      return template
        ? `Scope: ${repositoryName} › ${template.displayName}${this.state.showTextSearch ? " · OCR text available" : ""}`
        : `Scope: ${repositoryName} › all ${formatCount(templateCount)} index templates · Block and Lot fields`;
    }
    if (template) return `Scope: ${repositoryName} › ${template.displayName} (restricted to one template)`;
    return `Scope: ${repositoryName} › all ${formatCount(templateCount)} index templates (global)`;
  };

  resultsSummary = () => {
    const rows = this.currentResults();
    const { mode, lastTextQuery } = this.state;
    if (mode === SEARCH_MODES.INDEX) {
      const info = this.state.blockLotInfo;
      if (rows.length && this.state.indexResultKind === "blockLot" && info) {
        const asked = [info.block ? `Block ${info.block}` : "", info.lot ? `Lot ${info.lot}` : ""].filter(Boolean).join(", ");
        return `${formatCount(rows.length)} results for ${asked} across ${formatCount(info.templatesSearched)} index templates${
          info.isTruncated ? " · more records match: choose one repository or fill in both fields" : ""
        }`;
      }
      return rows.length ? `${formatCount(rows.length)} results` : "";
    }
    if (mode === SEARCH_MODES.TEXT) {
      return rows.length ? `${formatCount(rows.length)} results for “${lastTextQuery}” · sorted by score` : "";
    }
    if (mode === SEARCH_MODES.SEMANTIC) {
      // Shown above the grid (renderSemanticHeader), as in the design.
      return "";
    }
    return "";
  };

  toolbarForMode = (mode) => {
    const p = this.props.props || {};
    const singleTemplate = Boolean(this.state.scopeTemplateName);
    return {
      onColumns: mode === SEARCH_MODES.INDEX && singleTemplate ? this.handleFilePropertiesPopup : null,
      // Expunge fills its form from the record's own index fields, which only Index Search rows of one template hold.
      showExpunge: this.state.client.length > 0 && mode === SEARCH_MODES.INDEX && this.state.indexResultKind === "template" && singleTemplate,
      onExpunge: this.handleExpungementPopup,
      showRename: Boolean(p.hasManageFiles),
      onRename: this.handleRenameFile,
      showEmail: Boolean(p.hasSendEmail),
      onEmail: this.handleEmailPopup,
      showDownload: Boolean(p.hasDownloadIndex),
      onDownload: this.handleDownloadIndexCsv,
      showUpload: Boolean(p.hasManageFiles),
      onUpload: this.handleUploadFiles,
      onRenameSave: this.handleRenameFileSave,
      onRenameCancel: () => this.handleRenameFileCancel(false),
    };
  };

  isLocked = (mode) => {
    if (!AI_MODES.includes(mode)) return false;
    if (this.state.deniedReason) return true;
    return this.aiState(mode) !== "live";
  };

  renderCriteria() {
    const { mode, entitlement } = this.state;
    const template = this.selectedTemplate();
    if (mode === SEARCH_MODES.TEXT) {
      return (
        <TextSearchPanel
          query={this.state.textQuery}
          matchMode={this.state.textMatchMode}
          highlightInViewer={this.state.textHighlight}
          templateName={this.state.scopeTemplateName}
          templateDisplayName={template ? template.displayName : ""}
          isLoading={this.state.isLoading}
          onQueryChange={(textQuery) => this.setState({ textQuery })}
          onMatchModeChange={(textMatchMode) => this.setState({ textMatchMode })}
          onHighlightChange={(textHighlight) => this.setState({ textHighlight })}
          onSubmit={this.runTextSearch}
          onReset={this.handleTextReset}
        />
      );
    }
    if (mode === SEARCH_MODES.SEMANTIC) {
      return (
        <SemanticSearchPanel
          query={this.state.semanticQuery}
          isLoading={this.state.aiSearching}
          hasResults={Boolean(this.state.aiSession) || this.currentResults().length > 0}
          entitlement={entitlement}
          onQueryChange={(semanticQuery) => this.setState({ semanticQuery })}
          onSubmit={this.runSemanticSearch}
          onClear={this.startNewAiConversation}
        />
      );
    }
    if (mode === SEARCH_MODES.ASSISTANT) {
      return (
        <AiAssistantPanel
          entitlement={entitlement}
          hasSession={Boolean(this.state.aiSession)}
          onNewConversation={this.startNewAiConversation}
          question={this.state.assistantQuestion}
          onQuestionChange={(assistantQuestion) => this.setState({ assistantQuestion })}
          onAsk={this.runAssistantQuestion}
          isLoading={this.state.aiSearching}
          conversation={this.state.conversation}
        />
      );
    }
    return (
      <IndexSearchPanel
        columns={this.state.columns}
        search={this.state.search}
        templateSelected={Boolean(template)}
        templateDisplayName={template ? template.displayName : ""}
        showTextSearch={this.state.showTextSearch}
        isLoading={this.state.isLoading}
        readOnly={this.state.renameMode}
        onChange={this.handleSearchChange}
        onSubmit={this.runIndexSearch}
        onReset={this.handleIndexReset}
        blockLot={this.state.blockLot}
        blockLotError={this.state.blockLotError}
        onBlockLotChange={this.handleBlockLotChange}
        onBlockLotSubmit={this.runBlockLotSearch}
      />
    );
  }

  // One ChatSearch for both AI tabs, always at the same place in the tree, so its session (results, follow-ups,
  // summaries, report) survives moving between Semantic Search, AI Assistant and the other tabs. Hidden, not
  // unmounted, while another tab is shown.
  renderEmbeddedChat(visible) {
    if (!this.aiChatAvailable()) return null;
    const { mode, chatMode } = this.state;
    const className = visible && mode === SEARCH_MODES.ASSISTANT ? "ds-results ds-results-chat" : "ds-results-hidden";
    return (
      <div className={className} aria-hidden={!visible}>
        <ChatSearch
          ref={(ref) => (this.chatSearchRef = ref)}
          embedded
          mode={chatMode}
          scope={this.scopeForChatSearch()}
          searchProps={this.props.props}
          aiEnabled={this.aiChatAvailable()}
          hideComposer={chatMode !== CHAT_SEARCH_MODE_ASSISTANT}
          onDocuments={this.handleSemanticDocuments}
          onSearching={(aiSearching) => this.setState({ aiSearching })}
          onSessionChange={this.handleSessionChange}
          onEntitlementDenied={this.handleEntitlementDenied}
          onViewDocument={this.handleChatViewDocument}
          designLayout
          showDocumentsInline={false}
          onConversationChange={this.handleConversationChange}
          onSuggestedSearch={this.aiState(SEARCH_MODES.SEMANTIC) === "live" ? this.handleSuggestedSearch : undefined}
        />
      </div>
    );
  }

  // Above the Semantic grid (design): the summary line, the notes the server's response calls for, and what the
  // search was interpreted as.
  renderSemanticHeader() {
    const rows = this.currentResults();
    const meta = this.state.semanticMeta;
    if (!meta && !rows.length) return null;
    const notes = semanticResultsNotes(meta);
    const chips = interpretedAs(meta && meta.searchPlan, this.state.templates);
    const summary = semanticResultsSummary(rows, meta);
    return (
      <div className="ds-semantic-header">
        {summary && <p className="ds-semantic-summary">{summary}</p>}
        {notes.map((note) => (
          <p key={note} className="ds-note">
            {note}
          </p>
        ))}
        {rows.length > 0 && (
          <SearchHelpCard
            help={meta && meta.searchHelp}
            templates={this.state.templates}
            onSearch={this.handleSuggestedSearch}
            disabled={this.state.aiSearching}
          />
        )}
        {chips.length > 0 && (
          <div className="ds-interpreted" aria-label="Interpreted as">
            <span className="ds-interpreted-title">Interpreted as</span>
            {chips.map((chip) => (
              <span key={chip.label} className="ds-interpreted-chip">
                {chip.label}: <strong>{chip.value}</strong>
              </span>
            ))}
            <button type="button" className="ds-link-button" onClick={this.handleRefine}>
              Not right? Refine
            </button>
          </div>
        )}
      </div>
    );
  }

  // Under the Semantic grid (design): the search's own "broader matches" and "more results" actions, then the way to
  // the AI Assistant with the same records.
  renderSemanticFooter() {
    const rows = this.currentResults();
    const meta = this.state.semanticMeta || {};
    if (!rows.length) return null;
    const noExact = String(meta.responseType || "").toUpperCase() === "NO_EXACT_MATCH_WITH_BROADER_RESULTS";
    const broader = !noExact && meta.hasBroaderMatches && meta.broaderMatchCount > 0;
    const canAsk = Boolean(this.state.aiSession) && this.aiState(SEARCH_MODES.ASSISTANT) === "live";
    return (
      <div className="ds-semantic-footer">
        {(broader || meta.hasMoreMatches) && (
          <div className="ds-semantic-more">
            {broader && (
              <button type="button" className="btn ds-btn-secondary" disabled={this.state.aiSearching}
                onClick={() => this.runChatAction("SHOW_BROADER_MATCHES")}>
                {`Show ${formatCount(meta.broaderMatchCount)} broader ${meta.broaderMatchCount === 1 ? "match" : "matches"}`}
              </button>
            )}
            {meta.hasMoreMatches && (
              <button type="button" className="btn ds-btn-secondary" disabled={this.state.aiSearching}
                onClick={() => this.runChatAction("CHECK_MORE_RESULTS")}>
                Check more results
              </button>
            )}
          </div>
        )}
        {canAsk && (
          <div className="ds-ask-bar">
            <p>
              <strong>Need an answer rather than a list?</strong>{" "}
              {`Ask about these ${formatCount(rows.length)} ${rows.length === 1 ? "record" : "records"} - summaries, an answer with citations, or a report.`}
            </p>
            <button type="button" className="btn ds-btn-primary" onClick={this.handleAskAboutResults}>
              Open in AI Assistant
            </button>
          </div>
        )}
      </div>
    );
  }

  renderResults() {
    const { mode, renameMode } = this.state;
    if (mode === SEARCH_MODES.ASSISTANT) return null;
    const rows = this.currentResults();
    const template = this.selectedTemplate();
    const busy = mode === SEARCH_MODES.SEMANTIC ? this.state.aiSearching : this.state.isLoading;
    const emptyHint = this.state.searched[mode]
      ? "No Files Available"
      : mode === SEARCH_MODES.INDEX
        ? template
          ? `Enter criteria on the left and choose Search to list ${template.displayName} files.`
          : "Enter a Block, a Lot or both on the left to search every index template that has those fields, or choose one index template in the scope bar."
        : mode === SEARCH_MODES.TEXT
          ? template
            ? `Enter words or a phrase on the left to search the OCR text of ${template.displayName} files.`
            : "Enter words or a phrase on the left to search the OCR text of every index template you can access. Templates without OCR text return no results."
          : this.semanticSearchError()
            ? `The search didn't finish. Please try again. (${this.semanticSearchError()})`
            : this.state.semanticMeta
            ? this.semanticEmptyText()
            : "Describe what you are looking for on the left. Matching documents appear here in the order the search ranks them.";
    return (
      <Fragment>
        {mode === SEARCH_MODES.SEMANTIC && this.renderSemanticHeader()}
        {rows.length === 0 && !busy && (
          <div className="ds-empty" role="status">
            {emptyHint}
          </div>
        )}
        {/* The grid mounts with the first results: Tabulator must measure columns and rows while visible. */}
        {rows.length > 0 && (
          <ResultsGrid
            key={mode}
            ref={(ref) => (this.gridRef = ref)}
            mode={mode}
            columns={this.columnsForMode(mode, renameMode)}
            columnsKey={this.columnsKey(mode, renameMode)}
            data={rows}
            index="FileId"
            renameMode={renameMode}
            summary={this.resultsSummary()}
            toolbar={this.toolbarForMode(mode)}
          />
        )}
        {mode === SEARCH_MODES.SEMANTIC && this.renderSemanticFooter()}
      </Fragment>
    );
  }

  // The body keeps the same element structure in every mode (criteria, then the right column with the AI session
  // and the grid), so the shared AI session is never remounted by a tab change.
  renderModeBody() {
    const { mode, entitlement, entitlementLoading, deniedReason } = this.state;
    const aiMode = AI_MODES.includes(mode);
    const waiting = aiMode && entitlementLoading;
    const locked = aiMode && !entitlementLoading && this.isLocked(mode);
    const open = !waiting && !locked;
    return (
      <div className="ds-body" id="searchModePanel" role="tabpanel" aria-labelledby={`searchTab-${mode}`}>
        {open && <aside className="ds-left">{this.renderCriteria()}</aside>}
        <div className="ds-right">
          {locked && (
            <AiLockedPanel
              mode={mode}
              reason={deniedReason || aiTabLockReason(mode, entitlement)}
              entitlement={entitlement}
              hasTextSearch={this.textTabAvailable()}
              onSwitchMode={this.setMode}
              onRetry={this.loadEntitlement}
            />
          )}
          {waiting && (
            <div className="ds-loading" role="status">
              Checking your DocumentSync AI subscription…
            </div>
          )}
          {this.renderEmbeddedChat(open && aiMode)}
          {open && this.renderResults()}
        </div>
      </div>
    );
  }

  renderPopups() {
    const p = this.props.props || {};
    return (
      <Fragment>
        <Popup
          contentStyle={{ width: "95%", borderRadius: "15px", padding: "15px" }}
          open={this.state.viewerOpen}
          modal
          closeOnDocumentClick={false}
          onClose={this.closeViewer}
        >
          <div>
            {/* eslint-disable-next-line */}
            <a className="close" onClick={this.closeViewer}>
              &times;
            </a>
            <iframe id="imageViewer" src={config.ImageViewer} title="Image Viewer" style={{ width: "100%", height: "90vh" }} />
          </div>
        </Popup>
        {this.state.showEmailPopup && (
          <EmailPopup
            props={{
              showEmailPopup: this.state.showEmailPopup,
              fileIdArray: this.state.fileIdArray,
              manageRedaction: p.hasManageRedaction,
            }}
            unmountMe={this.handleChildUnmount}
          />
        )}
        {this.state.showExpungementPopup && (
          <ExpungementPopup
            props={{
              showExpungementPopup: this.state.showExpungementPopup,
              selectedRow: this.state.fileIdArray,
              client: this.state.client,
              templateName: this.state.scopeTemplateName,
            }}
            unmountMe={this.handleChildUnmount}
          />
        )}
        {this.state.showFilePropertiesPopup && (
          <FileProperties
            props={{
              showFilePropertiesPopup: this.state.showFilePropertiesPopup,
              columns: this.state.columns,
              userId: p.userId,
              templateName: this.state.scopeTemplateName,
              hasManageRedaction: p.hasManageRedaction,
              hasTextScore: p.hasTextSearch,
              hasFileProperties: p.hasFileProperties,
            }}
            unmountMe={this.handleChildUnmount}
          />
        )}
        {this.state.showAppendPrependPopup && (
          <AppendPrependPdf
            props={{
              filePath: this.state.fileName,
              fileIdArray: this.state.fileIdArray,
              showAppendPrependPopup: this.state.showAppendPrependPopup,
              email: p.loggedInUser,
            }}
            unmountMe={this.handleChildUnmount}
          />
        )}
      </Fragment>
    );
  }

  render() {
    const { mode } = this.state;
    return (
      <Fragment>
        <div id="mainPageDiv">
          <Header props={this.props.props} history={this.props.history} />
          <div className="SearchDocumentSync ds-search">
            <div className="ds-title-row">
              <div>
                <h1 className="ds-title">Search Repository</h1>
                <p className="ds-subtitle">
                  Every search mode stays on this screen. Choose a mode, set the scope, then search.
                </p>
              </div>
              <button type="button" className="btn ds-btn-secondary ds-browse" title="Browse Repository" onClick={this.handleBrowse}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                </svg>
                Browse Repository
              </button>
            </div>

            <SearchModeTabs
              mode={mode}
              onChange={this.setMode}
              entitlement={this.state.entitlement}
              hasTextSearch={this.textTabAvailable()}
              loadingEntitlement={this.state.entitlementLoading}
              tabState={this.aiState}
            />

            <ScopeBar
              mode={mode}
              repositories={this.state.repositories}
              templates={this.availableTemplates()}
              repositoryId={this.state.scopeRepositoryId}
              templateName={this.state.scopeTemplateName}
              onRepositoryChange={this.handleRepositoryChange}
              onTemplateChange={this.handleTemplateChange}
              summary={this.scopeSummary()}
              disabled={this.state.renameMode}
            />

            {this.renderModeBody()}
          </div>
        </div>
        {this.renderPopups()}
      </Fragment>
    );
  }
}

export default Search;
